import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { ChatMessage, DEOAgentAPIResponse, Feedback, SSEStatusEvent } from '../models/chat.models';
import { ConversationStreamEvent, DeoConversation } from '../models/deo-conversation.model';
import { buildTempSessionId, isTempSessionId } from '../models/temp-session.constants';
import { AIAgentService } from './ai-agent.service';
import { DeoAgentReplyService } from './deo-agent-reply.service';
import { AgentRunHistoryEntry } from 'app/modules/agent-library/models/agent-run.model';
import { getApiErrorMessage } from './api-error.helper';
import { SSEEvent } from './sse-client.service';
import { resolveStreamActivityPhase } from './stream-activity.helper';
import { applyProgressStatus, isProgressPhase, settleProgress } from './message-progress.helper';
import { readClarificationOptions } from './clarification.helper';

interface SendAttachment {
  name: string;
  size: number;
  type: string;
}

/** The agent answering a turn locally: its id picks the reply, its name is what the fallback says. */
interface AnsweringAgent {
  id: string;
  name: string;
}

/** Per-conversation streaming internals, kept out of the rendered DeoConversation so views never see them. */
interface StreamRuntime {
  subscription?: Subscription;
  pendingBuffer: string;
  activeStreamingMsg: ChatMessage | null;
  typingTimer?: ReturnType<typeof setInterval>;
  completeData: { message: ChatMessage; data: DEOAgentAPIResponse; originalMessage: string } | null;
  streamedSessionId: string | null;
}

const TITLE_MAX_LENGTH = 50;
const DRAIN_INTERVAL_MS = 20;
const DRAIN_FAST_THRESHOLD = 200;
const DRAIN_FAST_CHARS = 8;
const DRAIN_SLOW_CHARS = 2;

/**
 * Owns every chat conversation and the streaming/typewriter engine, decoupled from any view.
 *
 * Because this is a root singleton, an in-flight response keeps streaming while the user navigates between
 * chats, switches tabs, or moves between the floating widget and the full screen. Surfaces hold a
 * DeoConversation by reference and render its `messages`; this service mutates that same array in place as
 * tokens arrive, so returning to a conversation shows it exactly as it was left and continues seamlessly.
 */
@Injectable({
  providedIn: 'root'
})
export class DeoConversationService {
  private readonly _conversations = new Map<string, DeoConversation>();
  private readonly _runtimes = new Map<string, StreamRuntime>();
  private readonly _drafts = new Map<string, string>();
  private readonly _streamEvents$ = new Subject<ConversationStreamEvent>();

  readonly streamEvents$: Observable<ConversationStreamEvent> = this._streamEvents$.asObservable();

  constructor(
    private readonly _aiAgentService: AIAgentService,
    private readonly _agentReplyService: DeoAgentReplyService
  ) {}

  /** Creates a fresh, empty conversation for a brand-new chat (no persisted session id yet). */
  startConversation(): DeoConversation {
    const conversation: DeoConversation = {
      clientId: crypto.randomUUID(),
      sessionId: null,
      messages: [],
      status: 'idle'
    };
    this._conversations.set(conversation.clientId, conversation);
    return conversation;
  }

  /**
   * Returns the given conversation when nothing has happened in it yet, so opening a fresh composer over
   * and over binds to one conversation instead of leaving an unused one behind on every click.
   */
  reuseOrStartConversation(conversation: DeoConversation | null | undefined): DeoConversation {
    if (conversation && !conversation.sessionId && conversation.status === 'idle' && conversation.messages.length === 0) {
      return conversation;
    }
    return this.startConversation();
  }

  /**
   * The unsent text of each conversation, keyed by `clientId` so a draft typed in a new chat survives
   * the temp -> real session id swap. Kept in memory only: a page reload clears every draft.
   */
  getDraft(clientId: string | null | undefined): string {
    return clientId ? (this._drafts.get(clientId) ?? '') : '';
  }

  setDraft(clientId: string | null | undefined, text: string): void {
    if (!clientId) {
      return;
    }
    if (text) {
      this._drafts.set(clientId, text);
    } else {
      this._drafts.delete(clientId);
    }
  }

  /** Returns the live conversation for a session id if one exists in the store (streaming or already loaded). */
  getConversationBySessionId(sessionId: string): DeoConversation | undefined {
    for (const conversation of this._conversations.values()) {
      if (conversation.sessionId === sessionId) {
        return conversation;
      }
    }
    return undefined;
  }

  /**
   * Returns the conversation for a session, creating it and lazily loading its history from the backend
   * the first time. An existing conversation (live stream or previously loaded) is returned untouched, so
   * a chat the user is mid-response on is never clobbered by a refetch.
   */
  openSession(sessionId: string): DeoConversation {
    const existing = this.getConversationBySessionId(sessionId);
    if (existing) {
      return existing;
    }

    const conversation: DeoConversation = {
      clientId: crypto.randomUUID(),
      sessionId,
      messages: [],
      status: 'idle'
    };
    this._conversations.set(conversation.clientId, conversation);
    this.loadHistory(conversation);
    return conversation;
  }

  private loadHistory(conversation: DeoConversation): void {
    if (!conversation.sessionId || isTempSessionId(conversation.sessionId)) {
      return;
    }

    this._aiAgentService.getChatMessages(conversation.sessionId).subscribe({
      next: response => {
        if (response.succeeded && conversation.status !== 'streaming') {
          conversation.messages = response.data ?? [];
          this._emitUpdated(conversation.clientId);
          this._loadFeedback(conversation);
        }
      },
      error: error => {
        this._streamEvents$.next({ type: 'error', clientId: conversation.clientId, error: getApiErrorMessage(error, 'Failed to load chat messages') });
      }
    });
  }

  /**
   * Restores the thumbs up/down a user previously gave in this session.
   *
   * Ratings come from their own endpoint and are applied after the messages already render, so a
   * feedback-side failure leaves the conversation intact with unrated thumbs instead of breaking the
   * chat. Nothing is emitted as an error for the same reason: the ratings are decorative.
   */
  private _loadFeedback(conversation: DeoConversation): void {
    if (!conversation.sessionId) {
      return;
    }

    this._aiAgentService.getSessionFeedback(conversation.sessionId).subscribe({
      next: response => {
        if (!response.succeeded || !response.data?.length) {
          return;
        }
        this._applyFeedback(conversation, response.data);
        this._emitUpdated(conversation.clientId);
      },
      error: () => {
        // Keep the conversation as loaded; the thumbs simply stay unset.
      }
    });
  }

  private _applyFeedback(conversation: DeoConversation, feedback: Feedback[]): void {
    const ratingByMessageId = new Map(feedback.map(entry => [entry.message_id, entry.is_positive]));

    conversation.messages.forEach(message => {
      const rating = message.id ? ratingByMessageId.get(message.id) : undefined;
      if (rating !== undefined) {
        message.isPositiveFeedback = rating;
      }
    });
  }

  /** True while the given conversation is streaming a response from the backend, which is what can be stopped. */
  isStreaming(conversation: DeoConversation | null | undefined): boolean {
    return conversation?.status === 'streaming';
  }

  /** True while the conversation is producing any answer, streamed or answered locally by an agent. */
  isBusy(conversation: DeoConversation | null | undefined): boolean {
    return !!conversation && conversation.status !== 'idle';
  }

  /**
   * Sends a user message in the conversation and starts streaming the assistant response.
   * Pushes the user message and a streaming placeholder into `conversation.messages` (mutated in place),
   * then drives the typewriter as tokens arrive.
   */
  send(conversation: DeoConversation, messageContent: string, attachment?: SendAttachment): void {
    const trimmed = messageContent.trim();
    if (!trimmed || this.isBusy(conversation)) {
      return;
    }

    conversation.status = 'streaming';

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content: trimmed,
      role: 'user',
      timestamp: new Date(),
      attachment
    };
    conversation.messages.push(userMessage);

    // History must be captured before the streaming placeholder is added.
    const historyMessages = [...conversation.messages];

    const streamingMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      isTyping: true,
      isStreaming: true
    };
    conversation.messages.push(streamingMessage);

    const runtime = this._createRuntime(conversation.clientId);
    const sessionIdToSend = isTempSessionId(conversation.sessionId) ? null : conversation.sessionId;
    const projectIdToSend = sessionIdToSend === null ? (conversation.projectId ?? null) : null;

    this._registerNewChat(conversation, trimmed);
    this._emitUpdated(conversation.clientId);

    runtime.subscription = this._aiAgentService.sendMessageStream(sessionIdToSend, trimmed, historyMessages, projectIdToSend).subscribe({
      next: (event: SSEEvent) => this._handleStreamEvent(conversation, streamingMessage, trimmed, event),
      error: err => this._handleStreamError(conversation, streamingMessage, err?.message || 'Failed to send message. Please try again.')
    });
  }

  /**
   * Runs a turn a named agent answers, instead of sending it to the backend.
   *
   * There is nothing to send it to: `DEOAgentMessageRequest` carries no agent, so a question put to
   * one agent has no route. The conversation mechanics are `send`'s — the user message, a typing
   * placeholder, a busy status and `updated` ticks — so every surface's loading states work the same.
   * What it deliberately does not do is register a chat session: a locally answered turn is not a
   * chat the backend knows about, so it belongs in no session list.
   */
  sendToAgent(conversation: DeoConversation, messageContent: string, agent: AnsweringAgent): void {
    const trimmed = messageContent.trim();
    if (!trimmed || this.isBusy(conversation)) {
      return;
    }

    conversation.status = 'answering';
    conversation.messages.push({ id: crypto.randomUUID(), content: trimmed, role: 'user', timestamp: new Date() });

    const pending: ChatMessage = { id: crypto.randomUUID(), content: '', role: 'assistant', timestamp: new Date(), isTyping: true };
    conversation.messages.push(pending);
    this._emitUpdated(conversation.clientId);

    this._agentReplyService.fetchAgentReply(agent.id).subscribe(reply => {
      pending.isTyping = false;

      if (reply) {
        pending.content = reply.answer;
        pending.agentReply = reply;
      } else {
        pending.content = `${agent.name} has nothing on that yet. Switch to DEO AI Assist to ask more widely.`;
      }

      conversation.status = 'idle';
      this._emitUpdated(conversation.clientId);
    });
  }

  /**
   * Opens a recorded run as a conversation of its own.
   *
   * A run is a transcript, not an empty thread, and it is not a chat session either — it gets a fresh
   * conversation with no session id, so nothing tries to sync or reload it from the backend. Busy while
   * the transcript loads, for the same reason a turn is.
   */
  openAgentRun(agent: AnsweringAgent, run: AgentRunHistoryEntry): DeoConversation {
    const conversation = this.startConversation();
    conversation.status = 'answering';
    this._emitUpdated(conversation.clientId);

    this._agentReplyService.fetchRunConversation(agent.id, agent.name, run).subscribe(messages => {
      conversation.messages.push(...messages);
      conversation.status = 'idle';
      this._emitUpdated(conversation.clientId);
    });

    return conversation;
  }

  /**
   * Turns a brand-new conversation into a chat the session lists can show, which is why sending the first
   * message is what creates a sidebar entry and clicking New Chat is not.
   *
   * The temporary id also makes the conversation addressable while its first answer streams, so the user
   * can open another chat and come back to this one. It is swapped for the real id on completion.
   *
   * Must stay synchronous: hosts switch to the chat view and call `send` in the same event handler, so the
   * view is only created once this has run. Deferring it would render that view with no session id.
   */
  private _registerNewChat(conversation: DeoConversation, trimmed: string): void {
    if (conversation.sessionId) {
      return;
    }

    conversation.sessionId = buildTempSessionId(conversation.clientId);
    this._streamEvents$.next({
      type: 'registered',
      clientId: conversation.clientId,
      sessionId: conversation.sessionId,
      title: this._deriveTitle(trimmed)
    });
  }

  private _handleStreamEvent(conversation: DeoConversation, streamingMessage: ChatMessage, originalMessage: string, event: SSEEvent): void {
    if (event.type === 'token' || event.type === 'status') {
      this._recordStreamedSessionId(conversation.clientId, event.session_id);
    }

    if (event.type === 'token') {
      this._handleToken(conversation, streamingMessage, event.delta);
    } else if (event.type === 'status') {
      this._handleStatus(conversation, streamingMessage, event);
    } else if (event.type === 'complete') {
      this._handleComplete(conversation, streamingMessage, originalMessage, event.data);
    } else if (event.type === 'error') {
      this._handleStreamError(conversation, streamingMessage, event.error, event.status);
    }
    // heartbeat: keep showing the typing indicator, no action needed.
  }

  private _handleToken(conversation: DeoConversation, streamingMessage: ChatMessage, delta: string): void {
    const runtime = this._runtimes.get(conversation.clientId);
    if (!runtime) {
      return;
    }
    streamingMessage.isTyping = false;
    streamingMessage.content += delta;
    runtime.pendingBuffer += delta;
    this._ensureTypingDrain(conversation, streamingMessage);
  }

  /**
   * Records what the backend is doing so the view can say what the wait is for. An unrecognised phase
   * leaves the message untouched: newer backends may report phases this build knows nothing about.
   */
  private _handleStatus(conversation: DeoConversation, streamingMessage: ChatMessage, event: SSEStatusEvent): void {
    if (isProgressPhase(event.phase)) {
      this._handleProgressStatus(conversation, streamingMessage, event);
      return;
    }

    const activity = resolveStreamActivityPhase(event.phase);
    if (!activity) {
      return;
    }

    streamingMessage.isTyping = false;
    streamingMessage.activity = activity;
    this._emitUpdated(conversation.clientId);
  }

  /**
   * The notes and lookups the agent reports before its first token (DATA-1629). `isTyping` stays on:
   * these arrive while the answer is still being worked out, and the dots are the only liveness signal
   * between a note and the lookup it announces.
   */
  private _handleProgressStatus(conversation: DeoConversation, streamingMessage: ChatMessage, event: SSEStatusEvent): void {
    const progressSteps = applyProgressStatus(streamingMessage.progressSteps, event);
    if (progressSteps === streamingMessage.progressSteps) {
      return;
    }

    streamingMessage.progressSteps = progressSteps;
    this._emitUpdated(conversation.clientId);
  }

  private _handleComplete(conversation: DeoConversation, streamingMessage: ChatMessage, originalMessage: string, data: DEOAgentAPIResponse): void {
    const runtime = this._runtimes.get(conversation.clientId);
    if (runtime?.typingTimer) {
      // Let the drain finish, then finalize once the buffer empties.
      runtime.completeData = { message: streamingMessage, data, originalMessage };
    } else {
      this._finalizeStreamedMessage(conversation, streamingMessage, data, originalMessage);
    }
  }

  /**
   * Stops the in-flight response, settling the half-finished message and returning the conversation to idle.
   * The cancelled question goes back into the draft, so the user can edit it without copying it first.
   * If no answer came back, it also leaves the chat, so sending the draft does not show and send it twice.
   * A question with an attachment stays, because the draft holds only text.
   */
  stop(conversation: DeoConversation): void {
    if (conversation.status !== 'streaming') {
      return;
    }

    const runtime = this._runtimes.get(conversation.clientId);
    if (runtime?.completeData) {
      this._finishAnsweredTurn(conversation, runtime.completeData);
      return;
    }

    const cancelledUserMessage = this._findCancelledUserMessage(conversation);
    const cancelledPrompt = cancelledUserMessage?.content ?? '';
    const streamedSessionId = runtime?.streamedSessionId ?? null;
    runtime?.subscription?.unsubscribe();
    const agentProducedNothing = this._settleInterruptedMessage(conversation);
    this._disposeRuntime(conversation.clientId);
    const promptRestored = this._restoreCancelledPrompt(conversation.clientId, cancelledPrompt);
    if (agentProducedNothing && promptRestored && !cancelledUserMessage?.attachment) {
      this._removeMessage(conversation, cancelledUserMessage);
    }
    conversation.status = 'idle';
    this._resolveStoppedNewChat(conversation, streamedSessionId, cancelledPrompt);
    this._emitUpdated(conversation.clientId);
  }

  /**
   * The backend already sent `complete` and only the typing animation is left, so the answer is not
   * cancelled: show the rest of the text at once and finish the turn with its persisted id and artifacts.
   */
  private _finishAnsweredTurn(conversation: DeoConversation, completeData: NonNullable<StreamRuntime['completeData']>): void {
    this._flushTypingDrain(conversation.clientId);
    this._finalizeStreamedMessage(conversation, completeData.message, completeData.data, completeData.originalMessage);
    this._emitUpdated(conversation.clientId);
  }

  private _recordStreamedSessionId(clientId: string, sessionId: string | undefined): void {
    const runtime = this._runtimes.get(clientId);
    if (runtime && sessionId) {
      runtime.streamedSessionId = sessionId;
    }
  }

  /**
   * A first turn stopped after the backend sent its session id already has a real session. Taking that id
   * makes a retry continue the same session, instead of `send` creating a second one and leaving the first
   * without a list entry. The `completed` event swaps the temp list rows exactly as a finished turn does.
   */
  private _resolveStoppedNewChat(conversation: DeoConversation, streamedSessionId: string | null, prompt: string): void {
    const previousSessionId = conversation.sessionId;
    if (!streamedSessionId || !isTempSessionId(previousSessionId)) {
      return;
    }

    conversation.sessionId = streamedSessionId;
    this._linkConversationToProject(conversation);
    this._streamEvents$.next({
      type: 'completed',
      clientId: conversation.clientId,
      sessionId: streamedSessionId,
      isNew: true,
      title: this._deriveTitle(prompt),
      artifactCount: 0,
      previousSessionId
    });
  }

  /** `send` pushes the user message and the streaming placeholder as a pair, so the question is the message just before it. */
  private _findCancelledUserMessage(conversation: DeoConversation): ChatMessage | undefined {
    const streamingIndex = conversation.messages.findIndex(message => message.isStreaming);
    const userMessage = streamingIndex > 0 ? conversation.messages[streamingIndex - 1] : undefined;
    return userMessage?.role === 'user' ? userMessage : undefined;
  }

  /** Never replaces text the user already typed in the composer. */
  private _restoreCancelledPrompt(clientId: string, prompt: string): boolean {
    if (!prompt || this.getDraft(clientId)) {
      return false;
    }
    this.setDraft(clientId, prompt);
    return true;
  }

  private _removeMessage(conversation: DeoConversation, message: ChatMessage | undefined): void {
    conversation.messages = conversation.messages.filter(candidate => candidate !== message);
  }

  /**
   * Freeze the in-flight assistant message, keeping any partial answer or dropping an empty placeholder.
   * Returns true when the placeholder was dropped because the agent produced nothing.
   */
  private _settleInterruptedMessage(conversation: DeoConversation): boolean {
    this._flushTypingDrain(conversation.clientId);

    const streamingMessage = conversation.messages.find(message => message.isStreaming);
    if (!streamingMessage) {
      return false;
    }

    streamingMessage.isStreaming = false;
    streamingMessage.isTyping = false;
    streamingMessage.activity = null;
    streamingMessage.progressSteps = settleProgress(streamingMessage.progressSteps);

    const agentProducedNothing = !streamingMessage.content;
    if (agentProducedNothing) {
      this._removeMessage(conversation, streamingMessage);
    }
    return agentProducedNothing;
  }

  private _finalizeStreamedMessage(conversation: DeoConversation, streamingMessage: ChatMessage, data: DEOAgentAPIResponse, originalMessage: string): void {
    streamingMessage.isStreaming = false;
    streamingMessage.isTyping = false;
    streamingMessage.activity = null;
    streamingMessage.progressSteps = settleProgress(streamingMessage.progressSteps);

    // A `streaming_unavailable` fallback sends no token events, so the text only arrives on `complete`.
    if (!streamingMessage.content?.trim() && data.content) {
      streamingMessage.content = data.content;
      streamingMessage.displayedContent = data.content;
    }

    this._adoptServerMessageId(streamingMessage, data.message_id);
    streamingMessage.clarificationOptions = readClarificationOptions(data.clarification, data.options);

    if (data.artifacts && data.artifacts.length > 0) {
      streamingMessage.artifacts = this._aiAgentService.convertHTMLArtifactsToUI(data.artifacts, data.session_id, streamingMessage.id);
    }

    const previousSessionId = conversation.sessionId;
    const isNew = !previousSessionId || isTempSessionId(previousSessionId);
    if (isNew && data.session_id) {
      conversation.sessionId = data.session_id;
    }

    if (isNew) {
      this._linkConversationToProject(conversation);
    }

    this._disposeRuntime(conversation.clientId);
    conversation.status = 'idle';

    this._streamEvents$.next({
      type: 'completed',
      clientId: conversation.clientId,
      sessionId: conversation.sessionId ?? data.session_id,
      isNew,
      title: this._deriveTitle(originalMessage),
      artifactCount: data.artifacts?.length ?? 0,
      previousSessionId
    });
  }

  /**
   * Replaces the placeholder id given to the streaming message with the one the backend persisted.
   *
   * A message is created locally before the backend has stored it, so `send` seeds it with a client-side
   * UUID that exists nowhere else. Anything keyed by message id — rating it, linking its artifacts — has to
   * use the stored id instead, or it points at a row that does not exist: rating a freshly streamed answer
   * was rejected with "message not found", and the thumb was gone on the next reload.
   *
   * The id is absent when the backend runs without message persistence; the placeholder then stays, which
   * keeps the message renderable and its `trackBy` stable even though there is nothing to rate.
   */
  private _adoptServerMessageId(message: ChatMessage, serverMessageId: string | null | undefined): void {
    if (serverMessageId) {
      message.id = serverMessageId;
    }
  }

  private _handleStreamError(conversation: DeoConversation, streamingMessage: ChatMessage, errorMsg: string, status?: number): void {
    if (this._isRejectedBeforeStream(status)) {
      this._dropUnsentUserMessage(conversation, streamingMessage);
    }
    this._flushTypingDrain(conversation.clientId);
    streamingMessage.isStreaming = false;
    streamingMessage.isTyping = false;
    streamingMessage.activity = null;
    streamingMessage.progressSteps = undefined;
    if (!streamingMessage.content) {
      conversation.messages = conversation.messages.filter(message => message !== streamingMessage);
    }
    this._disposeRuntime(conversation.clientId);
    conversation.status = 'idle';
    this._streamEvents$.next({ type: 'error', clientId: conversation.clientId, error: errorMsg, status });
  }

  /** 403 is a viewer replying in a chat shared read-only, 404 a chat the user can no longer reach; neither stored the message. */
  private _isRejectedBeforeStream(status?: number): boolean {
    return status === 403 || status === 404;
  }

  private _dropUnsentUserMessage(conversation: DeoConversation, streamingMessage: ChatMessage): void {
    const index = conversation.messages.indexOf(streamingMessage);
    const userMessage = conversation.messages[index - 1];
    if (index > 0 && userMessage.role === 'user') {
      conversation.messages = conversation.messages.filter(message => message !== userMessage);
    }
  }

  /** Reads the text to render for a message: the typed-out portion while streaming, the full content otherwise. */
  getDisplayedContent(message: ChatMessage): string {
    if (message.isStreaming && message.displayedContent !== undefined) {
      return message.displayedContent;
    }
    return message.content;
  }

  /**
   * Links a freshly created chat to its project once it has a real session id. Runs in the service (not a
   * view), so the link is persisted even if the user left the project folder before the response finished.
   */
  private _linkConversationToProject(conversation: DeoConversation): void {
    const sessionId = conversation.sessionId;
    if (!conversation.projectId || !sessionId || isTempSessionId(sessionId)) {
      return;
    }
    this._aiAgentService.setSessionProject(sessionId, conversation.projectId).subscribe({
      error: () => {
        // Best-effort: the chat was created; it just isn't linked to the project yet.
      }
    });
  }

  private _ensureTypingDrain(conversation: DeoConversation, message: ChatMessage): void {
    const runtime = this._runtimes.get(conversation.clientId);
    if (!runtime || runtime.typingTimer) {
      return;
    }
    runtime.activeStreamingMsg = message;
    message.displayedContent ??= '';

    runtime.typingTimer = setInterval(() => {
      if (runtime.pendingBuffer) {
        const charsToAdd = runtime.pendingBuffer.length > DRAIN_FAST_THRESHOLD ? DRAIN_FAST_CHARS : DRAIN_SLOW_CHARS;
        const chunk = runtime.pendingBuffer.slice(0, charsToAdd);
        runtime.pendingBuffer = runtime.pendingBuffer.slice(charsToAdd);
        message.displayedContent = (message.displayedContent ?? '') + chunk;
        this._emitUpdated(conversation.clientId);
      } else if (runtime.completeData) {
        clearInterval(runtime.typingTimer!);
        runtime.typingTimer = undefined;
        runtime.activeStreamingMsg = null;
        const { message: msg, data, originalMessage } = runtime.completeData;
        runtime.completeData = null;
        this._finalizeStreamedMessage(conversation, msg, data, originalMessage);
      }
    }, DRAIN_INTERVAL_MS);
  }

  private _flushTypingDrain(clientId: string): void {
    const runtime = this._runtimes.get(clientId);
    if (!runtime) {
      return;
    }
    if (runtime.typingTimer) {
      clearInterval(runtime.typingTimer);
      runtime.typingTimer = undefined;
    }
    if (runtime.activeStreamingMsg && runtime.pendingBuffer) {
      runtime.activeStreamingMsg.displayedContent = (runtime.activeStreamingMsg.displayedContent ?? '') + runtime.pendingBuffer;
    }
    runtime.pendingBuffer = '';
    runtime.activeStreamingMsg = null;
    runtime.completeData = null;
  }

  private _createRuntime(clientId: string): StreamRuntime {
    const runtime: StreamRuntime = {
      pendingBuffer: '',
      activeStreamingMsg: null,
      completeData: null,
      streamedSessionId: null
    };
    this._runtimes.set(clientId, runtime);
    return runtime;
  }

  private _disposeRuntime(clientId: string): void {
    const runtime = this._runtimes.get(clientId);
    if (!runtime) {
      return;
    }
    if (runtime.typingTimer) {
      clearInterval(runtime.typingTimer);
    }
    this._runtimes.delete(clientId);
  }

  private _deriveTitle(message: string): string {
    return message.length > TITLE_MAX_LENGTH ? message.substring(0, TITLE_MAX_LENGTH) + '...' : message;
  }

  private _emitUpdated(clientId: string): void {
    this._streamEvents$.next({ type: 'updated', clientId });
  }
}
